import { GameType } from '@/types/games';

// Configuración de puntos para lecciones
export const LESSON_POINTS = {
  BASIC: 5,
  INTERMEDIATE: 10,
  ADVANCED: 15
} as const;

// Configuración de puntos para juegos
export const GAME_POINTS = {
  // Juego de Monedas
  COINS: {
    MIN_POINTS: 1,
    MAX_POINTS: 5,
    POINTS_PER_SCORE: 50 // 1 Cerebrix cada 50 puntos
  },
  // Juego de Calculadora
  CALCULATOR: {
    CORRECT_ANSWER: 2, // Puntos por respuesta correcta
    STREAK_BONUS: 1,   // Bonus por racha de 3 o más
    MAX_POINTS: 10     // Máximo por sesión
  },
  // Juego de Memoria
  MEMORY: {
    MATCH_PAIR: 10,      // Puntos por encontrar un par
    COMPLETE_LEVEL: 50,  // Bonus por completar el nivel
    PERFECT_TIME: 25,    // Bonus por completar en menos de 60 segundos
    NO_MISTAKES: 30,     // Bonus por completar sin errores
    MIN_POINTS: 100      // Mínimo para ganar Cerebrix
  },
  // Juego de Presupuesto
  BUDGET: {
    COMPLETION: 1,      // Completar un presupuesto básico
    BALANCE: 2,         // Presupuesto balanceado (gastos <= ingresos)
    SAVINGS: 2,         // Incluir ahorro en el presupuesto
    CATEGORIES: 1,      // Usar todas las categorías correctamente
    MAX_POINTS: 5       // Máximo por presupuesto
  },
  // Juego de Ahorro
  SAVINGS: {
    COMPLETION: 25,      // Completar una meta de ahorro
    SMART_GOAL: 30,      // Meta SMART (específica, medible, alcanzable, relevante, temporal)
    STRATEGY: 30,        // Buena estrategia de ahorro
    CONSISTENCY: 15,     // Ahorros consistentes
    MAX_POINTS: 100      // Máximo por juego
  }
} as const;

// Función para determinar el nivel de una lección
export function getLessonLevel(difficulty: number): keyof typeof LESSON_POINTS {
  if (difficulty >= 8) return 'ADVANCED';
  if (difficulty >= 5) return 'INTERMEDIATE';
  return 'BASIC';
}

// Función para obtener puntos de una lección
export function getLessonPoints(difficulty: number): number {
  const level = getLessonLevel(difficulty);
  return LESSON_POINTS[level];
}

// Función para calcular puntos del juego de monedas
export function calculateCoinsGamePoints(score: number, timeBonus: number): number {
  const totalScore = score + timeBonus;
  const points = Math.floor(totalScore / GAME_POINTS.COINS.POINTS_PER_SCORE);
  return Math.min(
    GAME_POINTS.COINS.MAX_POINTS,
    Math.max(GAME_POINTS.COINS.MIN_POINTS, points)
  );
}

// Función para calcular puntos del juego de calculadora
export function calculateCalculatorPoints(correctAnswers: number, maxStreak: number): number {
  const basePoints = correctAnswers * GAME_POINTS.CALCULATOR.CORRECT_ANSWER;
  const streakBonus = maxStreak >= 3 ? GAME_POINTS.CALCULATOR.STREAK_BONUS : 0;
  return Math.min(GAME_POINTS.CALCULATOR.MAX_POINTS, basePoints + streakBonus);
}

// Función para calcular puntos del juego de memoria
export function calculateMemoryPoints(stats: {
  completionTime: number,
  totalTime: number,
  fastMatches: number,
  maxStreak: number,
  errors: number
}): number {
  let points = 0;

  // Puntos por pares encontrados (8 pares en total)
  points += (8 * GAME_POINTS.MEMORY.MATCH_PAIR);

  // Bonus por completar el nivel
  points += GAME_POINTS.MEMORY.COMPLETE_LEVEL;

  // Bonus por tiempo perfecto (menos de 60 segundos)
  if (stats.completionTime <= 60) {
    points += GAME_POINTS.MEMORY.PERFECT_TIME;
  }

  // Bonus por no cometer errores
  if (stats.errors === 0) {
    points += GAME_POINTS.MEMORY.NO_MISTAKES;
  }

  // Convertir puntos a Cerebrix (redondeando hacia abajo)
  return Math.floor(points / 100);
}

// Función para calcular puntos del juego de presupuesto
export function calculateBudgetPoints(stats: {
  isBalanced: boolean,
  hasSavings: boolean,
  categoriesUsed: number,
  totalCategories: number,
  errorCount: number
}): number {
  let points = GAME_POINTS.BUDGET.COMPLETION;

  if (stats.isBalanced) {
    points += GAME_POINTS.BUDGET.BALANCE;
  }

  if (stats.hasSavings) {
    points += GAME_POINTS.BUDGET.SAVINGS;
  }

  if (stats.categoriesUsed / stats.totalCategories >= 0.8) {
    points += GAME_POINTS.BUDGET.CATEGORIES;
  }

  return Math.min(points, GAME_POINTS.BUDGET.MAX_POINTS);
}

// Función para calcular puntos del juego de ahorro
export interface SavingsPointsParams {
  goalCompleted: boolean;
  hasSmartGoal: boolean;
  hasStrategy: boolean;
  consistentSavings: boolean;
  timeUsed: number;
  maxTime: number;
}

export function calculateSavingsPoints(stats: SavingsPointsParams): number {
  let points = 0;

  if (stats.goalCompleted) {
    points += GAME_POINTS.SAVINGS.COMPLETION;
  }

  if (stats.hasSmartGoal) {
    points += GAME_POINTS.SAVINGS.SMART_GOAL;
  }

  if (stats.hasStrategy) {
    points += GAME_POINTS.SAVINGS.STRATEGY;
  }

  if (stats.consistentSavings) {
    points += GAME_POINTS.SAVINGS.CONSISTENCY;
  }

  return Math.min(points, GAME_POINTS.SAVINGS.MAX_POINTS);
}

// Constantes globales del sistema de Cerebrix
export const CEREBRIX_CONSTANTS = {
  DAILY_LIMIT: 20,           // Límite diario para todos los usuarios
  POINTS_PER_CEREBRIX: 100,  // Puntos necesarios para ganar 1 Cerebrix
  MIN_POINTS_REQUIRED: 100   // Puntos mínimos para empezar a ganar Cerebrix
} as const;

// Función para calcular Cerebrix basado en puntos y número de intentos
export function calculateCerebrixReward(
  quizPoints: number,
  totalPoints: number,
  attempts: number
): number {
  // Determinar el divisor basado en el número de intentos
  const pointsPerCerebrix = attempts === 1 ? 10 :  // 1er intento: 10 puntos = 1 Cerebrix
                           attempts === 2 ? 15 :  // 2do intento: 15 puntos = 1 Cerebrix
                           20;                    // 3er intento o más: 20 puntos = 1 Cerebrix

  // Calcular Cerebrix basado en puntos totales
  const cerebrix = Math.floor(totalPoints / pointsPerCerebrix);

  // Asegurar que siempre se gane al menos 1 Cerebrix si hay puntos
  return totalPoints > 0 ? Math.max(1, cerebrix) : 0;
}

// Configuración de cerebrix por tipo de juego
export interface CerebrixConfig {
  maxPoints: number;
  rules: {
    [key: string]: number;
  };
}

export const CEREBRIX_CONFIG: Record<GameType, CerebrixConfig> = {
  budget: {
    maxPoints: 5,
    rules: {
      completion: 1,
      efficiency: 2,
      strategy: 2
    }
  },
  savings: {
    maxPoints: 5,
    rules: {
      completion: 1,
      smart_goal: 2,
      strategy: 2
    }
  },
  business: {
    maxPoints: 5,
    rules: {
      completion: 1,
      profit: 2,
      strategy: 2
    }
  },
  calculator: {
    maxPoints: 5,
    rules: {
      completion: 1,
      accuracy: 2,
      speed: 2
    }
  },
  coins: {
    maxPoints: 5,
    rules: {
      collection: 1,
      combo: 2,
      speed: 2
    }
  },
  memory: {
    maxPoints: 5,
    rules: {
      completion: 1,
      pairs: 2,
      speed: 2
    }
  }
};
